import { useCallback, useState } from 'react';
import type { Result } from '@api';
import { Failure, Success, useErrorMessage, useDatahubAPI } from '@api';
import { useNotification } from '@data/notification';
import type { AxiosResponse, AxiosError } from 'axios';
import axios from 'axios';
import { projectAtom } from '@data/auth';
import { environmentAtom } from '@data/fms/environment/states';
import { useTranslation } from 'react-i18next';
import isNullOrUndefined from '@utils/isNullOrUndefined';
import type { ScanMatchingScore } from '@data/datahub/scanMatchingScore/types';
import { useAtomCallback } from 'jotai/utils';

type APIResponse = {
  location_metrics: ScanMatchingScore[];
  page_size: number;
  next_page_token?: string;
};

export const useSyncScanMatchingScoreAPI = (): {
  loading: boolean;
  getSyncScanMatchingScore: (
    vehicleId: string,
    params: URLSearchParams,
  ) => Promise<ScanMatchingScore[]>;
} => {
  const [loading, setLoading] = useState(false);
  const { notifyError } = useNotification();
  const getErrorMessage = useErrorMessage();
  const { t } = useTranslation();
  const { datahubAPI } = useDatahubAPI({ cancelWhenUnmounting: true });

  const request = useCallback(
    async (
      projectId: string,
      environmentId: string,
      vehicleId: string,
      params: URLSearchParams,
    ): Promise<Result<APIResponse, AxiosResponse>> => {
      try {
        const res = await datahubAPI.get(
          `/${projectId}/environments/${environmentId}/${
            vehicleId === 'all' ? '' : `vehicles/${vehicleId}/`
          }location_metrics?${params.toString()}`,
        );
        return new Success(res?.data);
      } catch (error) {
        return axios.isCancel(error)
          ? new Success({
              location_metrics: [],
              page_size: 0,
            })
          : new Failure((error as AxiosError).response as AxiosResponse);
      }
    },
    [datahubAPI],
  );

  const getSyncScanMatchingScore = useAtomCallback(
    useCallback(
      async (get, _, vehicleId: string, params: URLSearchParams) => {
        setLoading(true);
        const project = get(projectAtom);
        const environment = get(environmentAtom);

        if (!project || !environment) {
          notifyError({
            message: t('api.datahub.get_scan_matching_score', {
              status: 'failed',
            }),
          });
          setLoading(false);
          return [];
        }

        const req = async (requestParams: URLSearchParams) =>
          await request(
            project.id,
            environment.environment_id,
            vehicleId,
            requestParams,
          );

        const splitRequest = async (
          data: ScanMatchingScore[] = [],
        ): Promise<ScanMatchingScore[]> => {
          const res = await req(params);
          // 取得失敗時
          if (res?.isFailure()) {
            notifyError({
              message: `${t('api.datahub.get_scan_matching_score', {
                status: 'failed',
              })}: ${getErrorMessage(res)}`,
            });
            return data;
          }
          // 取得成功時
          if (res?.isSuccess()) {
            // 配列を結合
            const combinedData = [...data, ...res.value.location_metrics];
            if (isNullOrUndefined(res.value.next_page_token)) {
              return combinedData;
            }
            // レスポンスに next_page_token がある場合は次のリクエストを行う
            params.set('page_token', res.value.next_page_token);
            return await splitRequest(combinedData);
          }
          return data;
        };

        const data = await splitRequest();
        setLoading(false);
        return data;
      },
      [request, notifyError, t, getErrorMessage],
    ),
  );

  return {
    loading,
    getSyncScanMatchingScore,
  };
};
